import React, { useEffect } from "react";
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  BuildingStorefrontIcon,
  UserIcon,
  ChevronRightIcon,
  PhoneIcon,
  QueueListIcon,
  Square3Stack3DIcon,
  TruckIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { ArrowLeftIcon, PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { newOrderDetails } from "@/store/slices/newDeliverySlice";
import moment from "moment";
import ShowLoader from "@/components/Loader";
import useHasPermission from "@/components/useHasPermission";

const DeliveryStatus = ({ timeline, itemCount, collectedQty }) => {
  const { t } = useTranslation("delivery");

  const status = timeline?.toLowerCase();

  if (status === "delivered") {
    return (
      <span className="inline-flex items-center gap-1 bg-green-100 text-[#065C36] text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-200 uppercase tracking-wide ml-2">
        <CheckCircleIcon className="h-3 w-3" />
        {t("delivered")}
      </span>
    );
  }

  if (Number(collectedQty) > 0 && Number(collectedQty) === Number(itemCount)) {
    return (
      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-200 uppercase tracking-wide ml-2">
        <CheckCircleIcon className="h-3 w-3" />
        {t("collected")}
      </span>
    );
  }

  if (Number(collectedQty) > 0 && Number(collectedQty) < Number(itemCount)) {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200 uppercase tracking-wide ml-2">
        {t("partially_collected")}
      </span>
    );
  }

  return null;
};

export function AssignedDeliveryOverview() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSummaryPermission = useHasPermission("order.view");

  const { t } = useTranslation("delivery");

  /** Tab from URL so browser Back from drop-off / collect keeps the correct tab */
  const activeTab =
    searchParams.get("tab") === "client" ? "client" : "vendors";

  const setActiveTab = (tab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "client") {
          next.set("tab", "client");
        } else {
          next.delete("tab");
        }
        return next;
      },
      { replace: true }
    );
  };

  const { new_order_details, loading } = useSelector(
    (state) => state.delivery
  );
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (id) dispatch(newOrderDetails({ order_id: id }));
  }, [id, dispatch]);

  const priority = new_order_details?.delivery_locations?.[0]?.priority;

  const priorityColor =
    priority === "high"
      ? "bg-red-500"
      : priority === "medium"
      ? "bg-yellow-500"
      : "bg-green-500";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {loading && <ShowLoader />}

      {/* Header */}
      <div className="bg-[#065C36] text-white py-6 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto relative">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs sm:text-sm font-light text-left"
          >
            <ArrowLeftIcon className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">
              {t("back_to_assigned_order_listing")}
            </span>
            <span className="sm:hidden">{t("back")}</span>
          </button>

          <h1 className="text-xl sm:text-2xl font-semibold text-center mt-4 sm:mt-2">
            #{new_order_details?.order_id || ""}
          </h1>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="w-full max-w-7xl mx-auto py-6 sm:py-10 px-4 sm:px-6 space-y-6 flex-grow">

        {/* Created On Date Card */}
        <div className="flex items-center gap-3 bg-[#2F302C0F] rounded-xl px-5 py-2 shadow-sm border border-[rgba(47,48,44,0.06)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/img/icons/created.png"
              alt="Created Icon"
              className="h-5 w-5 object-contain shrink-0"
            />

            <p className="text-sm text-gray-600">
              {t("created_on")}{" "}
              <span className="font-semibold text-gray-800">
                {new_order_details?.order_created_date
                  ? moment(new_order_details?.order_created_date).format(
                      "DD/MM/YYYY - hh:mm A"
                    )
                  : ""}
              </span>
            </p>
          </div>

          <p className="text-sm text-gray-600"></p>
        </div>

        {/* Scheduled Date Card */}
        <div className="bg-gradient-to-r from-[#EBF2EF] to-[#FFFFFF] border border-gray-100 rounded-lg px-4 py-4 shadow-sm flex items-center gap-3 justify-between">

          {/* LEFT SIDE */}
          <div className="flex items-center gap-3">
            <div className="bg-white p-1 rounded-md">
              <CalendarDaysIcon className="h-4 w-4 text-[#065C36]" />
            </div>

            <p className="text-[13px] text-gray-600">
              {t("scheduled_on")}{" "}
              <span className="font-semibold text-gray-900">
                {[
                  ...new Set(
                    new_order_details?.delivery_locations?.map(
                      (d) => d?.deadline
                    )
                  ),
                ]
                  ?.filter(Boolean)
                  ?.map((date, i, arr) => (
                    <span key={i}>
                      {moment(date).format("DD/MM/YYYY - hh:mm A")}
                      {i !== arr.length - 1 ? ", " : ""}
                    </span>
                  ))}
              </span>
            </p>
          </div>

          {/* RIGHT SIDE: PRIORITY BADGE */}
          {priority && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">
                {t("priority")}:
              </span>

              <span
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase text-white shadow-sm ${priorityColor}`}
              >
                {priority}
              </span>
            </div>
          )}
        </div>

        {/* Cash Highlight Gradient Card */}
        <div
          className="rounded-xl px-6 py-5 text-white flex items-center"
          style={{
            background:
              "linear-gradient(90deg, rgba(244, 177, 31, 1) 0%, rgba(244, 177, 31, 0.6) 100%)",
          }}
        >
          <p className="text-sm font-medium">
            {t("amount_of_cash_to_be_collected")}
          </p>

          <h2 className="text-2xl font-bold tracking-tight ml-4">
            {new_order_details?.rfq_currency_code}{" "}
            {Number(
              new_order_details?.amount_to_be_collected || 0
            ).toFixed(2)}
          </h2>
        </div>

        {/* Charges Breakdown */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden text-[13px]">
          <div className="flex justify-between px-5 py-3 border-b border-gray-50">
            <span className="text-gray-900 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center opacity-60">
                <TruckIcon className="h-6 w-6 text-[rgba(6,92,54,1)]" />
              </div>

              {t("delivery_charges")}
            </span>

            <span className="font-semibold text-gray-700">
              {new_order_details?.rfq_currency_code}{" "}
              {Number(new_order_details?.delivery_charge || 0).toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between px-5 py-3 border-b border-gray-50">
            <span className="text-gray-900 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center opacity-60">
                <img
                  src="/img/icons/Container-3.png"
                  alt="drop"
                  className="h-3 w-3 text-[#2F302C] shrink-0"
                />
              </div>

              {t("installation_charges")}
            </span>

            <span className="font-semibold text-gray-700">
              {new_order_details?.rfq_currency_code}{" "}
              {Number(
                new_order_details?.installation_charge || 0
              ).toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between px-5 py-3 bg-[#2F302C0F]">
            <span className="text-gray-900 font-bold flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center opacity-60">
                <QueueListIcon className="h-4 w-4 text-[rgba(6,92,54,1)]" />
              </div>

              {t("total")}
            </span>

            <span className="font-bold text-[#065C36]">
              {new_order_details?.rfq_currency_code}{" "}
              {Number(
                new_order_details?.price_summary?.grand_total ??
                  ((Number(new_order_details?.delivery_charge) || 0) +
                    (Number(new_order_details?.installation_charge) || 0))
              ).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Recipient Details */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-[rgba(47,48,44,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-1 rounded-full bg-[#065C36]" />
            <h2 className="text-base font-bold text-gray-800">
              {t("recipient_details")}
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 text-sm text-gray-600">
            <p>
              <span className="text-gray-500">{t("name")} </span>
              <span className="font-semibold text-gray-800">
                {new_order_details?.recipient_details?.full_name || "N/A"}
              </span>
            </p>

            <p>
              <span className="text-gray-500">
                {t("mobile_number")}{" "}
              </span>

              <span className="font-semibold text-gray-800">
                {new_order_details?.recipient_details?.country_code || ""}{" "}
                {new_order_details?.recipient_details?.mobile_number ||
                  "N/A"}
              </span>
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-gray-100 pt-2 px-2 gap-8">
          <button
            onClick={() => setActiveTab("vendors")}
            className={`pb-2 flex items-center gap-2 text-sm font-bold transition-all border-b-2 ${
              activeTab === "vendors"
                ? "text-[#065C36] border-transparent"
                : "text-gray-400 border-transparent"
            }`}
          >
            <BuildingStorefrontIcon className="w-5 h-5" />
            {t("vendors")}
          </button>

          <button
            onClick={() => setActiveTab("client")}
            className={`pb-2 flex items-center gap-2 text-sm font-bold transition-all border-b-2 ${
              activeTab === "client"
                ? "text-[#065C36] border-transparent"
                : "text-gray-400 border-transparent"
            }`}
          >
            <UserIcon className="w-5 h-5" />
            {t("client")}
          </button>
        </div>

        {/* Vendor Listing */}
        {activeTab === "vendors" && (
          <div className="space-y-4">
            {new_order_details?.vendor_locations?.map((vendor, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-[#FDF3DD] to-white 
                          rounded-xl px-6 py-4 
                          border border-[#F3E6C7] 
                          shadow-sm relative"
              >
                {/* Top Right Action Icon */}
                <div
                  onClick={() => {
                    const lat = vendor?.latitude;
                    const lng = vendor?.longitude;

                    if (!lat || !lng) return;

                    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

                    window.open(googleMapsUrl, "_blank");
                  }}
                  className="absolute top-4 right-4 
                            bg-[#F4B11F] 
                            w-7 h-7 
                            flex items-center justify-center 
                            rounded-full text-white cursor-pointer hover:scale-105 transition"
                >
                  <PaperAirplaneIcon className="w-3.5 h-3.5 -rotate-45" />
                </div>

                {/* Vendor Name */}
                <h3 className="font-semibold text-[15px] text-gray-800">
                  {vendor?.company_name || "Riyadh Furniture Co."}

                  <DeliveryStatus
                    timeline={vendor?.delivery_timeline}
                    itemCount={vendor?.item_count}
                    collectedQty={vendor?.collected_quantity}
                  />
                </h3>

                {/* Info Row */}
                <div className="flex items-center justify-between mt-3 text-[13px]">

                  {/* LEFT SIDE: Phone + Address */}
                  <div className="flex items-start gap-16">

                    {/* Phone */}
                    <div className="flex items-center gap-2 text-gray-600">
                      <PhoneIcon className="w-4 h-4 text-[#065C36]" />
                      <span>{vendor?.phone_number || ""}</span>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-2 text-gray-600 max-w-[400px]">
                      <MapPinIcon className="w-4 h-4 text-[#065C36] mt-1 shrink-0" />

                      <div className="flex flex-col leading-tight">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {vendor?.pickup_location || ""}
                        </span>

                        {vendor?.pickup_landmark && (
                          <span className="text-xs text-gray-500 truncate">
                            {vendor?.pickup_landmark}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE: Items + View */}
                  <div className="flex items-center gap-56">
                    <div className="flex items-center gap-1 font-medium">
                      <Square3Stack3DIcon className="w-4 h-4 text-[#065C36]" />

                      <span className="text-gray-700">
                        {vendor?.item_count || 0} {t("items")}
                      </span>
                    </div>

                    <button
                      onClick={() =>
                        navigate(
                          `/delivery-partner/assigned-delivery/${id}/${vendor?.vendor_id}/collect`,
                          {
                            state: {
                              pickup_location: vendor?.pickup_location,
                              pickup_landmark: vendor?.pickup_landmark,
                              phone_number: vendor?.phone_number,
                              latitude: vendor?.latitude,
                              longitude: vendor?.longitude,
                              company_name: vendor?.company_name,
                            },
                          }
                        )
                      }
                      className="text-[#065C36] font-medium hover:underline"
                    >
                      {t("view_more")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "client" && (
          <>
            <div className="space-y-4">
              <div className="bg-white rounded-xl px-6 py-4 border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-[15px] text-gray-800">
                  {new_order_details?.customer_details?.company_name || ""}
                </h3>

                <div className="flex flex-wrap items-center justify-between mt-3 text-[13px] text-gray-600 gap-y-2">

                  {/* 1. Full Name */}
                  {(new_order_details?.customer_details?.is_name_visible ===
                    "yes" ||
                    hasSummaryPermission) && (
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-[#065C36]" />
                      <span>
                        {new_order_details?.customer_details?.full_name || ""}
                      </span>
                    </div>
                  )}

                  {/* 2. Phone Number */}
                  {(new_order_details?.customer_details
                    ?.is_mobile_number_visible === "yes" ||
                    hasSummaryPermission) && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="w-4 h-4 text-[#065C36]" />

                      <span>
                        {new_order_details?.customer_details?.phone_number}
                      </span>
                    </div>
                  )}

                  {/* 3. Email */}
                  {(new_order_details?.customer_details?.is_email_visible ===
                    "yes" ||
                    hasSummaryPermission) && (
                    <div className="flex items-center gap-2">
                      <EnvelopeIcon className="w-4 h-4 text-[#065C36]" />
                      <span>
                        {new_order_details?.customer_details?.email || ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Drop-off Locations */}
            <div className="space-y-4">
              {new_order_details?.delivery_locations?.map(
                (delivery, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-r from-[#FDF3DD] to-white rounded-xl px-6 py-4 border border-[#F3E6C7] shadow-sm relative"
                  >
                    <div
                      onClick={() => {
                        const lat = delivery?.latitude;
                        const lng = delivery?.longitude;

                        if (!lat || !lng) return;

                        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

                        window.open(googleMapsUrl, "_blank");
                      }}
                      className="absolute top-4 right-4 bg-[#F4B11F] w-7 h-7 flex items-center justify-center rounded-full text-white cursor-pointer hover:scale-105 transition"
                    >
                      <PaperAirplaneIcon className="w-3.5 h-3.5 -rotate-45" />
                    </div>

                    <h3 className="font-semibold text-[15px] text-gray-800">
                      {t("drop_off_location")} {index + 1}

                      <DeliveryStatus
                        timeline={delivery?.delivery_timeline}
                      />
                    </h3>

                    <div className="flex items-center justify-between mt-3 text-[13px]">
                      <div className="flex items-start gap-2">
                        <MapPinIcon className="w-4 h-4 text-[#065C36] mt-0.5" />

                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {delivery?.delivery_address || ""}
                          </span>

                          {delivery?.delivery_landmark && (
                            <span className="text-xs text-gray-500 truncate">
                              {delivery?.delivery_landmark}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-72">
                        <div className="flex items-center gap-1 text-[#065C36] font-medium">
                          <Square3Stack3DIcon className="w-4 h-4" />
                          {delivery?.item_count || 0} {t("items")}
                        </div>

                        <button
                          onClick={() =>
                            navigate(
                              `/delivery-partner/assigned-delivery/${delivery?.id}/dropoff`,
                              {
                                state: {
                                  drop_off_index: index + 1,
                                  delivery_address:
                                    delivery?.delivery_address,
                                  delivery_landmark:
                                    delivery?.delivery_landmark,
                                  phone_number: delivery?.phone_number,
                                  latitude: delivery?.latitude,
                                  longitude: delivery?.longitude,
                                },
                              }
                            )
                          }
                          className="text-[#065C36] font-medium hover:underline"
                        >
                          {t("view_more")}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Persistent Bottom Action Button */}
      <div className="w-full bg-[#065C36] py-6">
        <div className="max-w-7xl mx-auto"></div>
      </div>
    </div>
  );
}

export default AssignedDeliveryOverview;